'use client'

import {
  ColorTextDark,
  SizeRadiusRounded,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import {
  SizeSpacing1,
  SizeSpacing2,
  SizeSpacing3,
  SizeSpacing6,
  SizeSpacingHalf,
  SizeSpacingQuarter,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens-px'

import {
  type CSSProperties,
  type ForwardedRef,
  type MouseEvent,
  type TouchEvent,
  createElement,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isString from 'lodash/fp/isString'
import omit from 'lodash/fp/omit'

import { Typography } from '@/components/Typography/Typography'
import { Breakpoints, PREFIX } from '@/constants'
import useTheme from '@/hooks/useTheme'
import { cj, cn } from '@/styles/helpers'
import { onlyText } from '@/utils'

import type { ButtonProps } from './Button.types'

import {
  DEFAULT_VARIANT,
  GROUP_NAME,
  variantClassNames,
  variantDisabledClassNames,
  variantDisabledStyles,
  Variants,
  variantStyles,
} from './Button.constants'

/**
 * `Button` component is used to allow the user to make an interaction on either
 * a button or a link element.
 */
export const Button = forwardRef<
  HTMLAnchorElement | HTMLButtonElement | HTMLElement | null,
  ButtonProps
>(
  (
    {
      children,
      disableClickTracking = false,
      disabled = false,
      element,
      fullStyle = false,
      fullWidth = false,
      href,
      icon,
      iconHidden = false,
      iconOnly = false,
      iconPosition = 'right',
      inlineStyle = false,
      label,
      onClick,
      target,
      theme: themeOverride,
      truncate = true,
      type = 'button',
      underlined,
      variant = DEFAULT_VARIANT,
      wrap = false,
      wrapperClassName,
      wrapperStyles,
      ...props
    }: ButtonProps,
    ref?: ForwardedRef<
      HTMLAnchorElement | HTMLButtonElement | HTMLElement | null
    >,
  ) => {
    const theme = useTheme(themeOverride)

    const hasIcon = Boolean(icon)
    const hasChildren = Boolean(children)
    if (!hasChildren && !label) {
      console.warn(
        'You must provide a `label` or `children` to the `Button` component',
      )
    }

    const buttonRef = useRef<
      HTMLAnchorElement | HTMLButtonElement | HTMLElement
    >(null)
    useImperativeHandle<
      HTMLAnchorElement | HTMLButtonElement | HTMLElement | null,
      HTMLAnchorElement | HTMLButtonElement | HTMLElement | null
    >(ref, () => buttonRef.current)

    const handleClick = (
      event: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLElement>,
    ) => {
      if (!disabled && isFunction(onClick)) {
        onClick(event)
      }

      buttonRef.current?.blur()
    }

    const handleTouchStart = (event: TouchEvent<HTMLButtonElement>) => {
      if (isFunction(props.onTouchStart)) {
        props.onTouchStart(event)

        return
      }

      if ('ontouchstart' in document.documentElement && isFunction(onClick)) {
        onClick(
          event as unknown as MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
        )
      }
    }

    const handleTouchEnd = (event: TouchEvent<HTMLButtonElement>) => {
      if (isFunction(props.onTouchEnd)) {
        props.onTouchEnd(event)
      }

      if (
        'ontouchstart' in document.documentElement &&
        !isFunction(props.onTouchStart) &&
        isFunction(onClick)
      ) {
        event.preventDefault()
      }
    }

    const asLink = !isEmpty(href)
    const isTextVariant = variant === Variants.Text

    const isIconOnlyActiveAtBreakpoint = (breakpoint: string) => {
      if (iconOnly === false || iconOnly === 'xxs') {
        return false
      }

      if (iconOnly === true) {
        return true
      }

      const breakpoints = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']
      const iconOnlyIndex = breakpoints.indexOf(
        iconOnly === 'auto' ? 'md' : (iconOnly as string),
      )
      const currentIndex = breakpoints.indexOf(breakpoint)

      return currentIndex < iconOnlyIndex
    }

    const isIconHiddenActiveAtBreakpoint = (breakpoint: string) => {
      if (iconHidden === false) {
        return false
      }
      if (iconHidden === true || iconHidden === 'xxs') {
        return true
      }

      const breakpoints = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']
      const iconHiddenIndex = breakpoints.indexOf(iconHidden as string)
      const currentIndex = breakpoints.indexOf(breakpoint)

      return currentIndex < iconHiddenIndex
    }

    const getIconVisibilityClasses = () => {
      const iconOnlyActive = isIconOnlyActiveAtBreakpoint('xxs')
      const iconHiddenActive = isIconHiddenActiveAtBreakpoint('xxs')

      const shouldShowIcon =
        iconOnlyActive || (!iconOnlyActive && !iconHiddenActive)

      return shouldShowIcon ? 'flex' : 'hidden'
    }

    const getIconResponsiveClasses = () => {
      const classes: string[] = []
      const breakpoints = [
        { bp: 'xs', class: 'xs:flex', hideClass: 'xs:hidden' },
        { bp: 'sm', class: 'sm:flex', hideClass: 'sm:hidden' },
        { bp: 'md', class: 'md:flex', hideClass: 'md:hidden' },
        { bp: 'lg', class: 'lg:flex', hideClass: 'lg:hidden' },
        { bp: 'xl', class: 'xl:flex', hideClass: 'xl:hidden' },
        { bp: 'xxl', class: 'xxl:flex', hideClass: 'xxl:hidden' },
      ]

      let previousShouldShow =
        isIconOnlyActiveAtBreakpoint('xxs') ||
        (!isIconOnlyActiveAtBreakpoint('xxs') &&
          !isIconHiddenActiveAtBreakpoint('xxs'))

      breakpoints.forEach((bp) => {
        const iconOnlyActive = isIconOnlyActiveAtBreakpoint(bp.bp)
        const iconHiddenActive = isIconHiddenActiveAtBreakpoint(bp.bp)
        const shouldShow =
          iconOnlyActive || (!iconOnlyActive && !iconHiddenActive)

        if (shouldShow !== previousShouldShow) {
          if (shouldShow) {
            classes.push(bp.class)
          } else {
            classes.push(bp.hideClass)
          }
          previousShouldShow = shouldShow
        }
      })

      return classes.join(' ')
    }

    const getLabelHiddenClasses = () => {
      if (iconOnly === true) {
        return ['hidden']
      }
      if (iconOnly === false || iconOnly === 'xxs') {
        return []
      }

      const breakpointToHideClass: Record<string, string> = {
        auto: 'to-md:hidden',
        lg: 'to-lg:hidden',
        md: 'to-md:hidden',
        sm: 'to-sm:hidden',
        xl: 'to-xl:hidden',
        xs: 'to-xs:hidden',
        xxl: 'to-xxl:hidden',
        xxs: '',
      }

      const hideClass = breakpointToHideClass[iconOnly as string]

      return hideClass ? [hideClass] : []
    }

    const classNames = inlineStyle
      ? cn(
          `${PREFIX}-${GROUP_NAME} ${PREFIX}-${GROUP_NAME}--${variant}${asLink ? ` ${PREFIX}-${GROUP_NAME}__link` : ''}`,
          props.className,
        )
      : cn(
          `${PREFIX}-${GROUP_NAME}`,
          `${PREFIX}-${GROUP_NAME}--${variant}`,
          asLink
            ? `${PREFIX}-${GROUP_NAME}__link ${PREFIX}-${GROUP_NAME}__link--${variant}`
            : '',
          (asLink && !isTextVariant) || underlined === false
            ? 'no-underline'
            : '',
          'flex max-w-full items-center justify-center gap-2 rounded-full outline-none transition-colors duration-300 ease-out active:transition-none appearance-none box-border px-unset',
          wrap ? '' : 'max-h-6',
          isTextVariant ? 'min-h-3' : 'min-h-6',
          fullWidth ? `${PREFIX}-${GROUP_NAME}--full-width w-full` : 'w-fit',
          disabled
            ? `${PREFIX}-${GROUP_NAME}--disabled cursor-not-allowed ${variantDisabledClassNames[theme][variant]}`
            : `${variantClassNames[theme][variant]} cursor-pointer`,
          hasIcon
            ? `${PREFIX}-${GROUP_NAME}--with-addendum ${PREFIX}-${GROUP_NAME}--with-icon ${PREFIX}-${GROUP_NAME}--with-addendum-${iconPosition === 'left' ? 'prefix' : 'suffix'} ${PREFIX}-${GROUP_NAME}--with-icon-${iconPosition === 'left' ? 'prefix' : 'suffix'}`
            : '',
          hasIcon && iconOnly && iconOnly !== 'xxs'
            ? iconOnly === true
              ? `${PREFIX}-${GROUP_NAME}--icon-only`
              : iconOnly === 'auto'
                ? `${PREFIX}-${GROUP_NAME}--icon-only--auto ${PREFIX}-${GROUP_NAME}--icon-only--md`
                : `${PREFIX}-${GROUP_NAME}--icon-only--${String(iconOnly)}`
            : '',
          hasIcon && iconOnly && iconOnly !== 'xxs'
            ? iconOnly === true
              ? isTextVariant
                ? 'w-3 max-w-3 h-3 max-h-3 px-0'
                : 'w-6 max-w-6 max-h-6 h-6 px-0'
              : iconOnly === Breakpoints.xs
                ? isTextVariant
                  ? 'to-xs:w-3 to-xs:max-w-3 to-xs:h-3 to-xs:max-h-3 to-xs:px-0'
                  : 'to-xs:w-6 to-xs:max-w-6 to-xs:max-h-6 to-xs:h-6 to-xs:px-0'
                : iconOnly === Breakpoints.sm
                  ? isTextVariant
                    ? 'to-sm:w-3 to-sm:max-w-3 to-sm:h-3 to-sm:max-h-3 to-sm:px-0'
                    : 'to-sm:w-6 to-sm:max-w-6 to-sm:max-h-6 to-sm:h-6 to-sm:px-0'
                  : iconOnly === 'auto' || iconOnly === Breakpoints.md
                    ? isTextVariant
                      ? 'to-md:w-3 to-md:max-w-3 to-md:h-3 to-md:max-h-3 to-md:px-0'
                      : 'to-md:w-6 to-md:max-w-6 to-md:max-h-6 to-md:h-6 to-md:px-0'
                    : iconOnly === Breakpoints.lg
                      ? isTextVariant
                        ? 'to-lg:w-3 to-lg:max-w-3 to-lg:h-3 to-lg:max-h-3 to-lg:px-0'
                        : 'to-lg:w-6 to-lg:max-w-6 to-lg:max-h-6 to-lg:h-6 to-lg:px-0'
                      : iconOnly === Breakpoints.xl
                        ? isTextVariant
                          ? 'to-xl:w-3 to-xl:max-w-3 to-xl:h-3 to-xl:max-h-3 to-xl:px-0'
                          : 'to-xl:w-6 to-xl:max-w-6 to-xl:max-h-6 to-xl:h-6 to-xl:px-0'
                        : iconOnly === Breakpoints.xxl
                          ? isTextVariant
                            ? 'to-xxl:w-3 to-xxl:max-w-3 to-xxl:h-3 to-xxl:max-h-3 to-xxl:px-0'
                            : 'to-xxl:w-6 to-xxl:max-w-6 to-xxl:max-h-6 to-xxl:h-6 to-xxl:px-0'
                          : ''
            : '',
          props.className,
        )

    const style: CSSProperties | undefined = inlineStyle
      ? fullStyle
        ? {
            appearance: 'none',
            borderRadius: SizeRadiusRounded,
            boxSizing: 'border-box',
            display: asLink ? 'inline-block' : undefined,
            outline: 'none',
            paddingLeft: 'unset',
            paddingRight: 'unset',
            transition: 'color, background-color, border-color 300ms ease-out',
            ...(asLink && !isTextVariant ? { textDecoration: 'none' } : {}),
            ...(wrap ? {} : { maxHeight: SizeSpacing6 }),
            ...(isTextVariant
              ? { minHeight: SizeSpacing3 }
              : { minHeight: SizeSpacing6 }),
            ...(disabled
              ? {
                  cursor: 'not-allowed',
                  ...variantDisabledStyles[theme][variant],
                }
              : { cursor: 'pointer', ...variantStyles[theme][variant] }),
            ...(iconOnly && iconOnly !== 'xxs' && hasIcon
              ? {
                  height: SizeSpacing6,
                  maxHeight: SizeSpacing6,
                  maxWidth: SizeSpacing6,
                  padding: asLink
                    ? `calc(${SizeSpacing1} + ${SizeSpacingQuarter}) 0 0 calc(${SizeSpacing1} + ${SizeSpacingQuarter})`
                    : `${SizeSpacingHalf} 0 0 ${SizeSpacingQuarter}`,
                  width: SizeSpacing6,
                }
              : fullWidth
                ? {}
                : { width: 'fit-content' }),
            ...(iconOnly && iconOnly !== 'xxs' && hasIcon && isTextVariant
              ? {
                  height: SizeSpacing3,
                  maxHeight: SizeSpacing3,
                  maxWidth: SizeSpacing3,
                  width: SizeSpacing3,
                }
              : {}),
            ...(fullWidth ? { maxWidth: '100%', width: '100%' } : {}),
            ...(asLink && isTextVariant ? { color: ColorTextDark } : {}),
          }
        : {
            ...(wrap ? {} : { maxHeight: SizeSpacing6 }),
            ...(isTextVariant
              ? { minHeight: SizeSpacing3 }
              : { minHeight: SizeSpacing6 }),
            ...(disabled
              ? {
                  cursor: 'not-allowed',
                  ...variantDisabledStyles[theme][variant],
                }
              : { ...variantStyles[theme][variant] }),
            ...(iconOnly && iconOnly !== 'xxs' && hasIcon
              ? {
                  height: SizeSpacing6,
                  maxHeight: SizeSpacing6,
                  maxWidth: SizeSpacing6,
                  padding: asLink
                    ? `calc(${SizeSpacing1} + ${SizeSpacingQuarter}) 0 0 calc(${SizeSpacing1} + ${SizeSpacingQuarter})`
                    : `${SizeSpacingHalf} 0 0 ${SizeSpacingQuarter}`,
                  width: SizeSpacing6,
                }
              : fullWidth
                ? {}
                : { width: 'fit-content' }),
            ...(iconOnly && iconOnly !== 'xxs' && hasIcon && isTextVariant
              ? {
                  height: SizeSpacing3,
                  maxHeight: SizeSpacing3,
                  maxWidth: SizeSpacing3,
                  width: SizeSpacing3,
                }
              : {}),
            ...(fullWidth ? { maxWidth: '100%', width: '100%' } : {}),
          }
      : undefined

    const iconElement = (
      <div
        className={cj(
          `${PREFIX}-${GROUP_NAME}__icon`,
          `${PREFIX}-${GROUP_NAME}__icon--${iconPosition}`,
          !inlineStyle && 'h-3 w-3 items-center [&>svg]:h-3',
          !inlineStyle && asLink
            ? `${PREFIX}-${GROUP_NAME}__link__icon--${iconPosition}`
            : '',
          !inlineStyle && isTextVariant ? 'mt-0' : '',
          getIconVisibilityClasses(),
          getIconResponsiveClasses(),
        )}
        style={
          inlineStyle
            ? fullStyle
              ? {
                  boxSizing: 'border-box',
                  display: 'inline-block',
                  height: SizeSpacing3,
                  maxHeight: SizeSpacing3,
                  maxWidth: SizeSpacing3,
                  minHeight: SizeSpacing3,
                  minWidth: SizeSpacing3,
                  width: SizeSpacing3,
                  ...(isTextVariant ? { marginTop: 0 } : {}),
                  ...(iconPosition === 'left' &&
                  (!iconOnly || !hasIcon || iconOnly === 'xxs')
                    ? { marginRight: SizeSpacing2 }
                    : {}),
                  ...(iconPosition === 'right' &&
                  (!iconOnly || !hasIcon || iconOnly === 'xxs')
                    ? { marginLeft: SizeSpacing2 }
                    : {}),
                }
              : {
                  ...(iconPosition === 'left' &&
                  (!iconOnly || !hasIcon || iconOnly === 'xxs')
                    ? { marginRight: SizeSpacing2 }
                    : {}),
                  ...(iconPosition === 'right' &&
                  (!iconOnly || !hasIcon || iconOnly === 'xxs')
                    ? { marginLeft: SizeSpacing2 }
                    : {}),
                }
            : undefined
        }
      >
        {icon}
      </div>
    )

    const content = (
      <Typography
        className={cj(
          `${PREFIX}-${GROUP_NAME}__label`,
          `${PREFIX}-${GROUP_NAME}__label--${variant}`,
          !inlineStyle && asLink
            ? `${PREFIX}-${GROUP_NAME}__link__label ${PREFIX}-${GROUP_NAME}__link__label--${variant}`
            : '',
          !inlineStyle &&
            'flex max-h-full max-w-full flex-1 items-center justify-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap text-center align-middle',
          !inlineStyle && isTextVariant ? 'pt-0' : '',
          !inlineStyle && underlined === false ? 'no-underline' : '',
          wrapperClassName,
        )}
        element="div"
        inlineStyle={inlineStyle}
        style={
          inlineStyle
            ? fullStyle
              ? {
                  boxSizing: 'border-box',
                  marginTop: isTextVariant
                    ? '0'
                    : `calc(${SizeSpacingHalf} + ${SizeSpacingQuarter})`,
                  maxHeight: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textAlign: 'center',
                  textOverflow: 'ellipsis',
                  verticalAlign: 'middle',
                  whiteSpace: 'nowrap',
                  ...(isTextVariant ? { paddingTop: 0 } : {}),
                  ...(underlined === false ? { textDecoration: 'none' } : {}),
                  ...wrapperStyles,
                }
              : {
                  ...(underlined === false ? { textDecoration: 'none' } : {}),
                  ...wrapperStyles,
                }
            : undefined
        }
        variant={isTextVariant ? 'body-1-link' : 'body-1-median'}
      >
        {hasIcon && iconPosition === 'left' && iconElement}
        {iconOnly === true && hasIcon ? (
          false
        ) : hasChildren ? (
          iconOnly === false || iconOnly === 'xxs' || !hasIcon ? (
            children
          ) : (
            <div className={cj(...getLabelHiddenClasses())}>{children}</div>
          )
        ) : (
          <div
            className={
              inlineStyle
                ? undefined
                : cj(
                    wrap || truncate ? 'min-w-0' : '',
                    wrap ? 'whitespace-break-spaces' : '',
                    truncate ? 'truncate' : '',
                    ...getLabelHiddenClasses(),
                  )
            }
            style={
              inlineStyle
                ? {
                    boxSizing: 'border-box',
                    display: 'inline-block',
                    ...(wrap || truncate ? { minWidth: 0 } : {}),
                    ...(wrap ? { whiteSpace: 'break-spaces' } : {}),
                    ...(truncate
                      ? {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }
                      : {}),
                    ...(isTextVariant && underlined !== false
                      ? { textDecoration: 'underline' }
                      : {}),
                  }
                : undefined
            }
          >
            {label}
          </div>
        )}
        {hasIcon && iconPosition === 'right' && iconElement}
      </Typography>
    )

    const labelText = isString(label)
      ? label
      : isEmpty(label)
        ? onlyText(children)
        : onlyText(label)

    if (element && element !== 'a' && element !== 'button' && !asLink) {
      return createElement(
        element,
        // eslint-disable-next-line react-hooks/refs
        {
          ...(props.id === undefined ? {} : { id: props.id }),
          'aria-label': labelText,
          className: classNames,
          disabled,
          onClick: handleClick,
          ref: buttonRef as ForwardedRef<HTMLElement>,
          style: { ...style, ...props.style },
          title: labelText,
          ...omit(['className', 'style', 'id'], props),
        },
        content,
      )
    }

    if (asLink || element === 'a') {
      return (
        <a
          {...(props.id === undefined ? {} : { id: props.id })}
          ref={buttonRef as ForwardedRef<HTMLAnchorElement>}
          aria-label={labelText}
          className={classNames}
          href={href}
          {...(isEmpty(target) ? {} : { target })}
          style={{ ...style, ...props.style }}
          title={labelText}
          onClick={handleClick}
          {...omit(['className', 'id', 'style'], props)}
          {...(!isEmpty(href) && disableClickTracking
            ? { clicktracking: 'off' }
            : {})}
        >
          {content}
        </a>
      )
    }

    return (
      <button
        {...(props.id === undefined ? {} : { id: props.id })}
        ref={buttonRef as ForwardedRef<HTMLButtonElement>}
        aria-label={labelText}
        className={classNames}
        {...(props.dir === undefined
          ? {}
          : { dir: props.dir as 'ltr' | 'rtl' })}
        disabled={disabled}
        style={{ ...style, ...props.style }}
        title={labelText}
        type={type}
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
        {...omit(
          ['className', 'dir', 'id', 'style', 'onTouchEnd', 'onTouchStart'],
          props,
        )}
      >
        {content}
      </button>
    )
  },
)
Button.displayName = 'Button'

export default Button
